import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, ChartConfiguration, ChartData, ChartType, registerables } from 'chart.js';
import annotationPlugin, {AnnotationOptions} from 'chartjs-plugin-annotation';
import {FormsModule} from "@angular/forms";
import {PayTransparencyCategory} from "../../../../enums";
import { OrderPayTransparencyData } from '../../../../../typings';

const rulerPlugin = {
  id: 'rulerPlugin',
  beforeDraw(chart: any) {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;

    ctx.strokeStyle = 'rgba(14,13,13,0.2)';
    ctx.lineWidth = 1;

    const tickInterval = (xAxis.max - xAxis.min) / 20;
    for (let i = xAxis.min; i <= xAxis.max; i += tickInterval) {
      const x = xAxis.getPixelForValue(i);
      ctx.beginPath();
      ctx.moveTo(x, yAxis.bottom);
      ctx.lineTo(x, yAxis.bottom + 7);
      ctx.stroke();
    }

    ctx.strokeStyle = 'rgba(14,13,13,0.2)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(xAxis.left, yAxis.bottom);
    ctx.lineTo(xAxis.right, yAxis.bottom);
    ctx.stroke();
  }
};

const centerMessagePlugin = {
  id: 'centerMessagePlugin',
  afterDraw(chart: any) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const allZero = chart.data.datasets[0]?.data.every((v: number) => v === 0);
    if (allZero) {
      ctx.save();
      ctx.font = '350 12px Roboto';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const message = 'Check back later to see\nhow your rate compares.';
      const lines = message.split('\n');
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      const lineHeight = 14; // Adjust as needed

      lines.forEach((line, i) => {
        ctx.fillText(
          line,
          centerX,
          centerY + (i - (lines.length - 1) / 2) * lineHeight
        );
      });

      ctx.restore();
    }
  }
};

@Component({
  selector: 'app-pay-transparency-response-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, FormsModule],
  templateUrl: './pay-transparency-response-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pay-transparency-response-chart.component.scss']
})
export class  PayTransparencyResponseChartComponent implements OnInit, OnChanges {

  payTransparencyData: OrderPayTransparencyData;
  responseData: number[] = [];
  candidateRate: number = 0;
  xAxisLabel: string = 'Salary';
  payCategoryId: number = 1;
  minPayRate: number;
  maxPayRate: number;
  youBinIndex: number = -1;

  // Class properties for currency formatting
  // TODO - Update code to internationalize the currency formatting
  locale: string = 'en-US';
  currency: string = 'USD';

  @Input('payTransparencyData')
  set setPayTransparencyData(value: OrderPayTransparencyData) {
    if (value) {
      // Extract values from the object
      this.payCategoryId = value.payTransparencyCategoryId;
      if (this.payCategoryId === PayTransparencyCategory.HOURLY) {
        this.xAxisLabel = 'Hourly/Rate';
      } else if (this.payCategoryId === PayTransparencyCategory.DAILY) {
        this.xAxisLabel = 'Daily/Rate';
      } else if (this.payCategoryId === PayTransparencyCategory.SALARY) {
        this.xAxisLabel = 'Salary';
      } else if (this.payCategoryId === PayTransparencyCategory.PROJECT) {
        this.xAxisLabel = 'Project/Rate';
      }

      // Set appropriate min/max values based on category
      if (value.payTransparencyCategoryId === PayTransparencyCategory.SALARY) {
        this.minPayRate = value.minSalary;
        this.maxPayRate = value.maxSalary;
      } else {
        this.minPayRate = value.minRate;
        this.maxPayRate = value.maxRate;
      }

      this.tickPositions = this.getTickPositions();

      if (value.payRates && value.payRates.length > 0) {
        this.responseData = value.payRates;
      }

      // Update chart with new data
      this.updateChart();
    }
  }



  @Input('candidateRate')
  set setCandidateRate(value: number) {
    if (!value || value === 0) {
      this.candidateRate = 0;
      this.youBinIndex = -1;
    } else {
      this.candidateRate = Number(value.toString().replace(/,/g, ''));
    }
    // this.candidateRate = value;
    // this.candidateRate = Number(this.candidateRate.toLocaleString().replace(/,/g, ''));
    this.updateChart();
  }

  binCount = 13;
  tickPositions: { [key: number]: string } = {};
  inputRate: number = 0;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;


  public barChartOptions!: ChartConfiguration['options'];
  public barChartType: ChartType = 'bar';
  public barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [],
  };

  ngOnInit() {
    Chart.register(...registerables, annotationPlugin, rulerPlugin, centerMessagePlugin);
    this.tickPositions = this.getTickPositions();
    // TODO Remove this later. For now it may make testing easier.
    // this.responseData = [
    //   32, 51, 34, 38, 47, 49, 53, 35, 54,
    //   42, 53, 38, 35, 40, 27, 50, 55, 50,
    //   48, 46, 36, 54, 44, 43, 48, 33, 38,
    //   49, 49, 46, 53, 49, 53, 48, 33, 38,
    //   44, 42, 44, 49, 50, 24, 48, 51, 54,
    //   53, 44, 48, 42, 49, 34, 35, 34, 32,
    //   47, 51, 41, 43, 55, 27, 55, 34, 37,
    //   32, 51, 34, 38, 47, 49, 53, 35, 54,
    //   32, 51, 34, 38, 47, 49, 33, 35, 54,
    //   48, 37, 53, 30, 49, 45, 42, 47, 30,
    //   53, 44, 48, 42, 49, 34, 35, 54, 32,
    //   53, 45, 27, 31, 22, 40, 44, 40, 34, 45
    // ];
    this.initializeChartOptions();
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['responseData']) {
      this.updateChart();
    }

    if (changes['candidateRate']) {
      this.getYouMarkerConfig();
    }
  }

  initializeChartOptions() {
    this.barChartOptions = {
      responsive: true,
      animation: false,
      datasets: {
        bar: {
          barPercentage: 1.2,
          categoryPercentage: 0.8
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.xAxisLabel,
            color: "#000",
            font: {
              size: 10,
              weight: 450,
              family: 'Roboto',

            }
          },
          grid: {
            display: false,
          },
          border: {
            width: 2,
            color: 'lightgrey'
          },
          ticks: {
            stepSize: 1,
            callback: (value: number | string) => {
              const numericValue = typeof value === 'string' ? parseFloat(value) : value;
              const val = this.tickPositions[numericValue as keyof typeof this.tickPositions] || null;
              return val?.includes('\n') ? val.split('\n'): val;
            },
            font: {
              size: 10,
              weight: "bold"
            }
          },

        },
        y: {
          title: {
            display: true,
            text: 'Number of Responders',
            color: "#000",
            font: {
              size: 10,
              weight: 450,
              family: 'Roboto'
            },
          },
          display:true,
          grid: {
            display: false
          },
          beginAtZero: true,
          grace: '20%',
          ticks: {
            precision: 0,
            stepSize: 5
          },
          border: {
            width: 2,
            color: 'lightgrey'
          },

        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              return items[0].label;
            },
            label: (context) => {
              return `${context.parsed.y} applicants`;
            }
          }
        },
        annotation: {
          annotations: {
            verticalLine: this.getVerticalLineConfig(13.5, '', '#8b9393'),
            divisionLine: this.getVerticalLineConfig(0.5, '', '#8b9393'),
            ...(this.candidateRate > 0 ? { youMarker: this.getYouMarkerConfig() } : {})
          } as Record<string, AnnotationOptions>
        }
      }
    };
  }

  onRateChange() {
    this.candidateRate = this.inputRate;
    this.updateChart();
  }

  private getYouMarkerConfig(): AnnotationOptions | null {
    if (this.candidateRate <= 0) {
      return null;
    }
    const binSize = (this.maxPayRate  - this.minPayRate) / this.binCount;
    let binIndex = 0;

    if (this.candidateRate < this.minPayRate) {
      binIndex = 0;
    } else if (this.candidateRate > this.maxPayRate) {
      binIndex = this.binCount + 1;
    } else {
      binIndex = Math.min(
        Math.floor((this.candidateRate - this.minPayRate) / binSize),
        this.binCount - 1
      ) + 1;
    }

    this.youBinIndex = binIndex;

    const counts = this.calculateBucketCounts();
    const yValue = counts[binIndex] || 0;

    return {
      type: 'label',
      xValue: binIndex,
      yValue: yValue,
      content: ['You'],
      color: '#000',
      font: {
        size: 10,
        family: 'Roboto',
        weight: 'bold'
      },

      borderRadius: 20,
      xAdjust: 0,
      yAdjust: -11
    };
  }
  private getVerticalLineConfig(
    xPosition: number,
    labelText: string,
    color: string
  ): any {
    return {
      type: 'line',
      xMin: xPosition,
      xMax: xPosition,
      yMin: 0,
      yMax: (ctx: any) => ctx.chart.scales.y?.max || 100,
      borderColor: color,
      borderWidth: 1,
      borderDash: [2, 2],
      label: {
        content: labelText,
        display: true,
        position: 'top',
        backgroundColor: 'rgba(21,20,20,0.8)'
      }
    };
  }


  generateBucketColors(): string[] {
    const colors = [];

    if (this.youBinIndex === 0) {
      colors.push('#D32F2F');
    } else {
      colors.push ('rgba(245,129,154,0.7)');
    }

    for (let i = 1; i <= this.binCount; i++) {
      if (i === this.youBinIndex) {
        colors.push('#057AD7');
      } else {
        const ratio = i / this.binCount;
        colors.push(`rgba(54, 162, 235, ${0.3 + ratio * 0.1})`);
      }
    }

    const lastIndex = this.binCount + 1;
    if (this.youBinIndex === lastIndex) {
      colors.push('#D32F2F');
    } else {
      colors.push('rgba(245,129,154,0.7)');
    }

    return colors;
  }

  updateChart() {
    this.initializeChartOptions();
    this.barChartData = {
      labels: this.generateBucketLabels(),
      datasets: [{
        label: 'Number of Responders',
        data: this.calculateBucketCounts(),
        backgroundColor: this.generateBucketColors(),
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1
      }]
    };
    if (this.chart) {
      // Proper way to destroy and recreate
      this.chart.ngOnDestroy();
      this.chart.chart = undefined; // Use undefined instead of null
      this.chart.ngOnChanges({} as SimpleChanges);
    }
  }


  generateBucketLabels(): string[] {
    const binSize = (this.maxPayRate - this.minPayRate) / this.binCount;
    const labels = [`<${this.formatCurrency(this.minPayRate)}`];

    for (let i = 0; i < this.binCount; i++) {
      const lower = this.minPayRate + i * binSize;
      const upper = lower + binSize;
      labels.push(this.formatRange(lower, upper));
    }

    labels.push(`>${this.formatCurrency(this.maxPayRate)}`);
    return labels;
  }




  formatCurrency(value: number): string {
    // Helper function to format with Intl.NumberFormat
    const formatWithIntl = (val: number, maximumFractionDigits: number) => {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits
      }).format(val);
    };

    // For values over 1000 that need 'k' suffix
    const formatLargeValue = (val: number) => {
      const rounded = Math.floor(val / 100) / 10;
      const maxFractionDigits = rounded % 1 === 0 ? 0 : 1;
      return formatWithIntl(rounded, maxFractionDigits) + 'k';
    };

    // Determine format based on pay category and value
    if (this.payCategoryId === PayTransparencyCategory.HOURLY) {
      const maxFractionDigits = value % 1 === 0 ? 0 : 2;
      return formatWithIntl(value, maxFractionDigits);
    }
    else if (this.payCategoryId === PayTransparencyCategory.DAILY) {
      if (value < 1000) {
        const maxFractionDigits = value % 1 === 0 ? 0 : 2;
        return formatWithIntl(value, maxFractionDigits);
      } else {
        return formatLargeValue(value);
      }
    }
    // For SALARY or default case
    else {
      if (value < 1000) {
        return formatWithIntl(value, 0);
      } else {
        return formatLargeValue(value);
      }
    }
  }

  formatRange(lower: number, upper: number): string {
    return `${this.formatCurrency(lower)}-${this.formatCurrency(upper)}`;
  }

  getMaxValueWithPadding(): number {
    const counts = this.calculateBucketCounts();
    const maxCount = Math.max(...counts);
    return maxCount + 1;
  }

  calculateBucketCounts(): number[] {
    const binSize = (this.maxPayRate - this.minPayRate) / this.binCount;
    const counts = new Array(this.binCount + 2).fill(0);

    this.responseData.forEach(rate => {
      if (rate < this.minPayRate) {
        counts[0]++;
      } else if (rate > this.maxPayRate) {
        counts[counts.length - 1]++;
      } else {
        const binIndex = Math.min(
          Math.floor((rate - this.minPayRate) / binSize),
          this.binCount - 1
        ) + 1;
        counts[binIndex]++;
      }
    });

    if (this.youBinIndex !== -1) {
      counts[this.youBinIndex]++;
    }

    return counts;
  }

  getTickPositions(): { [key: number]: string } {
    const minMaxDifference = (this.maxPayRate - this.minPayRate);
    let rateText = this.payCategoryId === PayTransparencyCategory.SALARY ? 'Salary' : 'Rate';
    return {
      1: `${this.formatPayRateLabel(this.minPayRate)}\nMin ${rateText}`,
      4: `${this.formatPayRateLabel(minMaxDifference * 0.25 + this.minPayRate)}`,
      7: `${this.formatPayRateLabel(minMaxDifference * 0.5 + this.minPayRate)}`,
      10: `${this.formatPayRateLabel(minMaxDifference * 0.75 + this.minPayRate)}`,
      13: `${this.formatPayRateLabel(this.maxPayRate)}\nMax ${rateText}`,
    };
  }

  formatPayRateLabel(payRate: number): string {
    // For HOURLY rates
    if (this.payCategoryId === PayTransparencyCategory.HOURLY) {
      if (payRate % 1 === 0) {
        return payRate.toFixed(0);
      } else {
        return payRate.toFixed(2);
      }
    }
    // For DAILY rates
    else if (this.payCategoryId === PayTransparencyCategory.DAILY) {
      if (payRate < 1000) {
        if (payRate % 1 === 0) {
          return payRate.toFixed(0);
        } else {
          return payRate.toFixed(2);
        }
      } else {
        const rounded = Math.floor(payRate / 100) / 10;
        return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)) + 'k';
      }
    }
    // For SALARY or default case with larger values
    else {
      if (payRate < 1000) return payRate.toString();
      const rounded = Math.floor(payRate / 100) / 10;
      return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)) + 'k';
    }
  }

}
